import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, Clock, CheckCircle2, ChevronLeft, ChevronRight, Calendar, DollarSign, AlertCircle } from "lucide-react";

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function formatDate(date: Date | string | null) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTime(date: Date | string | null) {
  if (!date) return "";
  return new Date(date).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export default function MyCommissions() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [rangeStart, setRangeStart] = useState(() => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
  const [rangeEnd, setRangeEnd] = useState(() => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, "0")}`);

  const startDate = useMemo(() => new Date(`${rangeStart}T00:00:00`), [rangeStart]);
  const endDate = useMemo(() => new Date(`${rangeEnd}T23:59:59`), [rangeEnd]);

  const { data, isLoading, error } = (trpc as any).commissions.myReport.useQuery(
    { startDate, endDate },
    { refetchOnWindowFocus: false }
  );

  function selectMonth(nextYear: number, nextMonth: number) {
    const firstDay = new Date(nextYear, nextMonth, 1);
    const lastDay = new Date(nextYear, nextMonth + 1, 0);
    setYear(nextYear);
    setMonth(nextMonth);
    setRangeStart(`${nextYear}-${String(nextMonth + 1).padStart(2, "0")}-01`);
    setRangeEnd(`${nextYear}-${String(nextMonth + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`);
  }

  function prevMonth() {
    if (month === 0) selectMonth(year - 1, 11);
    else selectMonth(year, month - 1);
  }
  function nextMonth() {
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
    if (isCurrentMonth) return;
    if (month === 11) selectMonth(year + 1, 0);
    else selectMonth(year, month + 1);
  }

  const monthLabel = new Date(year, month, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();

  const rows: any[] = data?.rows ?? [];
  const adjustments: any[] = data?.adjustments ?? [];
  const summary = data?.summary ?? { commissionTotal: 0, pendingTotal: 0, paidTotal: 0 };
  const hasData = data?.professionalId != null;
  const pendingRows = rows.filter((row: any) => row.status === "pending");
  const firstHalf = pendingRows.filter((row: any) => new Date(row.createdAt).getDate() <= 15).reduce((total: number, row: any) => total + Number(row.commissionAmount ?? 0), 0);
  const secondHalf = pendingRows.filter((row: any) => new Date(row.createdAt).getDate() >= 16).reduce((total: number, row: any) => total + Number(row.commissionAmount ?? 0), 0);
  const payableTotal = Number(summary.totalToPay ?? summary.pendingTotal ?? 0);

  return (
    <div className="flex flex-col gap-4 p-4 pb-24 max-w-lg mx-auto">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3 pt-2">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-foreground">Minhas Comissões</h1>
          <p className="text-xs text-muted-foreground">Acompanhe seus ganhos</p>
        </div>
      </div>

      {/* Seletor de mês */}
      <div className="flex items-center justify-between bg-muted/40 rounded-xl px-4 py-2.5">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevMonth}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-semibold capitalize">{monthLabel}</span>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isCurrentMonth} onClick={nextMonth}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-muted-foreground">De
          <input type="date" value={rangeStart} max={rangeEnd} onChange={(event) => setRangeStart(event.target.value)} className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm text-foreground" />
        </label>
        <label className="text-xs text-muted-foreground">Até
          <input type="date" value={rangeEnd} min={rangeStart} onChange={(event) => setRangeEnd(event.target.value)} className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm text-foreground" />
        </label>
      </div>

      {/* Sem vínculo com profissional */}
      {!isLoading && !hasData && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex items-start gap-3 pt-4">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Sem vínculo profissional</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Sua conta não está vinculada a um profissional. Peça ao administrador para configurar seu perfil.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      )}

      {/* Cards de resumo */}
      {!isLoading && hasData && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-0 bg-primary/8">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-muted-foreground mb-1">1ª quinzena</p>
              <p className="text-base font-bold text-primary">{formatCurrency(firstHalf)}</p>
            </CardContent>
          </Card>
          <Card className="border-0 bg-amber-50">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-muted-foreground mb-1">2ª quinzena</p>
              <p className="text-base font-bold text-amber-600">{formatCurrency(secondHalf)}</p>
            </CardContent>
          </Card>
          <Card className="border-0 bg-green-50">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-muted-foreground mb-1">A receber</p>
              <p className="text-base font-bold text-green-600">{formatCurrency(payableTotal)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lista de comissões */}
      {!isLoading && hasData && rows.length === 0 && adjustments.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10">
            <DollarSign className="w-10 h-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground text-center">Nenhuma comissão registrada neste mês.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && hasData && rows.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">Atendimentos {rows.length}</h2>
          {rows.map((row: any) => (
            <Card key={row.id} className="border border-border/60">
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {row.clientName ?? "Cliente"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {row.serviceName ?? "Serviço"} · {row.percentage}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(row.createdAt)} {formatTime(row.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-sm font-bold text-foreground">
                      {formatCurrency(Number(row.commissionAmount))}
                    </span>
                    {row.status === "paid" ? (
                      <Badge className="text-[10px] h-5 bg-green-100 text-green-700 border-green-200 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Pago
                      </Badge>
                    ) : (
                      <Badge className="text-[10px] h-5 bg-amber-100 text-amber-700 border-amber-200 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Pendente
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Ajustes */}
      {!isLoading && hasData && adjustments.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">Ajustes</h2>
          {adjustments.map((adj: any) => (
            <Card key={adj.id} className="border border-border/60">
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {adj.description ?? (adj.type === "bonus" ? "Bônus" : "Desconto")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(adj.referenceDate)}
                    </p>
                  </div>
                  <span className={`text-sm font-bold shrink-0 ${adj.type === "bonus" ? "text-green-600" : "text-red-500"}`}>
                    {adj.type === "bonus" ? "+" : "-"}{formatCurrency(Math.abs(Number(adj.amount)))}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
